import { Roles, SubRolesOfAgency } from 'configs/roles';
import { CreateUserDto, UpdateUserDto } from 'services/openapi';

const optionalString = (value: unknown): string | undefined => {
  const text = value == null ? '' : String(value).trim();
  return text || undefined;
};

export const normalizeUserFilterRole = (
  role?: string,
): {
  role?: Exclude<CreateUserDto['role'], 'SpAdm'>;
  objectType?: CreateUserDto['objectType'];
} => {
  if (
    [Roles.AGENCY, Roles.ADMIN, Roles.MC, Roles.OIL_MC, Roles.ZO_MC].includes(
      role as Roles,
    )
  ) {
    return { role: role as Exclude<CreateUserDto['role'], 'SpAdm'> };
  }
  if ((Object.values(SubRolesOfAgency) as string[]).includes(role || '')) {
    return {
      role: Roles.AGENCY,
      objectType: role as CreateUserDto['objectType'],
    };
  }
  return {};
};

export const normalizeUserRole = (
  role: string,
  objectType?: string,
): Pick<CreateUserDto, 'role' | 'objectType'> => {
  const subRoles = Object.values(SubRolesOfAgency) as string[];
  if (subRoles.includes(role)) {
    return {
      role: Roles.AGENCY,
      objectType: role as CreateUserDto['objectType'],
    };
  }
  if (
    ![
      Roles.AGENCY,
      Roles.ADMIN,
      Roles.SUPER_ADMIN,
      Roles.MC,
      Roles.OIL_MC,
      Roles.ZO_MC,
    ].includes(role as Roles)
  ) {
    throw new Error('Vai trò không hợp lệ');
  }
  return {
    role: role as CreateUserDto['role'],
    objectType: (subRoles.includes(objectType || '')
      ? objectType
      : SubRolesOfAgency.STAFF) as CreateUserDto['objectType'],
  };
};

export const buildCreateUserPayload = (
  values: Record<string, any>,
): CreateUserDto => {
  const roleInfo = normalizeUserRole(values.role, values.objectType);
  const compId = optionalString(values.compId);
  if ([Roles.MC, Roles.OIL_MC].includes(roleInfo.role as Roles) && !compId) {
    throw new Error('Vui lòng chọn Doanh nghiệp/HTX cho Đối tác');
  }
  if ((optionalString(values.bidvVaPayerId)?.length || 0) > 16) {
    throw new Error('Tiểu khoản BIDV không được vượt quá 16 ký tự');
  }
  if ((optionalString(values.vaName)?.length || 0) > 200) {
    throw new Error('Tên tài khoản VA BIDV không được vượt quá 200 ký tự');
  }
  return {
    phoneNumber: String(values.phoneNumber || '')
      .trim()
      .replace(/^0/, '+84'),
    fullName: values.fullName,
    email: values.email || '',
    citizenIdentityCardNo: optionalString(values.citizenIdentityCardNo),
    ...roleInfo,
    ...(compId ? { compId } : {}),
    isActive: values.isActive,
    ...(optionalString(values.accountNumber)
      ? { accountNumber: optionalString(values.accountNumber) }
      : {}),
    ...(optionalString(values.bidvVaPayerId)
      ? { bidvVaPayerId: optionalString(values.bidvVaPayerId) }
      : {}),
    ...(optionalString(values.vaName)
      ? { vaName: optionalString(values.vaName) }
      : {}),
    adminActions:
      roleInfo.role === Roles.ADMIN ? values.adminActions || [] : [],
  };
};

export const buildUpdateUserPayload = (
  values: Record<string, any>,
): UpdateUserDto => ({
  userId: values.userId,
  fullName: values.fullName,
  email: values.email || '',
  citizenIdentityCardNo: optionalString(values.citizenIdentityCardNo),
  ...normalizeUserRole(values.role, values.objectType),
  ...(optionalString(values.compId)
    ? { compId: optionalString(values.compId) }
    : {}),
  isActive: values.isActive,
  ...(optionalString(values.bidvVaPayerId)
    ? { bidvVaPayerId: optionalString(values.bidvVaPayerId) }
    : {}),
  adminActions: values.role === Roles.ADMIN ? values.adminActions || [] : [],
});
